import { Alert, FormControl, MenuItem, Select, type SelectChangeEvent, TextField } from "@mui/material"
import { type FC } from "react"
import { useFormContext } from "react-hook-form"

import { UOM_PAIRS } from "../constants"
import { type PackagingInfoFormValues } from "../schemas/packaging"
import { PackagingInfoFormFieldIds, type PackagingUoms } from "../types/packaging"
import { getNumberTextFieldProps, getSelectFieldProps } from "../utils/packaging"

import { DimensionsFieldRow } from "./DimensionsFieldRow"

import { FieldGroup, FieldRow, Form, FormField } from "@/components/Forms"
import { ProductPackagingType, type UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { formatNumberString } from "@/utils/number"
import { getProductPackagingTypeLabel } from "@/utils/product"

interface PackagingInfoFormProps {
  uoms: UnitOfMeasurement[]
  disabled?: boolean
}

export const PackagingInfoForm: FC<PackagingInfoFormProps> = ({ disabled: disabledProp = false }) => {
  const {
    formState: { isSubmitting, errors },
    trigger,
    register,
  } = useFormContext<PackagingInfoFormValues>()

  const disabled = disabledProp || isSubmitting

  const renderPackagingSelectPlaceholder = (value: string) =>
    value ? (
      getProductPackagingTypeLabel(value as ProductPackagingType)
    ) : (
      <span className='text-gray-400'>Select One</span>
    )

  const handleUomSelectChange = (e: SelectChangeEvent<unknown>, fieldOnChange: (value: PackagingUoms) => void) => {
    const selectedPair = UOM_PAIRS.find((uom) => uom.id === e.target.value)
    if (selectedPair) {
      fieldOnChange(selectedPair.value)
    }
  }

  const renderUomSelectValue = (id: string) => {
    if (!id) {
      return <span className='text-gray-400'>Select One</span>
    }
    const uomPair = UOM_PAIRS.find((uom) => uom.id === id)
    return uomPair?.label
  }

  const formLevelError = errors.form?.message

  return (
    <Form className='gap-3'>
      <FieldGroup
        legend='Freight'
        data-testid='freight-section'
        className='gap-0'
        slotProps={{ legend: { className: "mb-4" } }}
      >
        {formLevelError && (
          <Alert severity='error' className='mb-4' data-testid='form-error-alert'>
            {formLevelError}
          </Alert>
        )}

        <FieldRow>
          <FormField
            name={PackagingInfoFormFieldIds.FreightItem}
            label='Freight Item'
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid={PackagingInfoFormFieldIds.FreightItem}
                {...getNumberTextFieldProps(fieldState, disabled, "000000")}
                value={field.value || ""}
                error={!!fieldState.error || !!formLevelError}
                onChange={(e) => {
                  const value = e.target.value
                  field.onChange(value || null)
                  trigger([PackagingInfoFormFieldIds.FreightItem, PackagingInfoFormFieldIds.FreightClass, "form"])
                }}
              />
            )}
          />
          <FormField
            name={PackagingInfoFormFieldIds.FreightClass}
            label='Freight Class'
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid={PackagingInfoFormFieldIds.FreightClass}
                {...getNumberTextFieldProps(fieldState, disabled, "00")}
                value={field.value || ""}
                error={!!fieldState.error || !!formLevelError}
                onChange={(e) => {
                  const value = e.target.value
                  field.onChange(value || null)
                  trigger([PackagingInfoFormFieldIds.FreightItem, PackagingInfoFormFieldIds.FreightClass, "form"])
                }}
              />
            )}
          />
        </FieldRow>
      </FieldGroup>

      <FieldGroup
        legend='Units of measure'
        data-testid='units-of-measure-section'
        className='gap-0'
        slotProps={{ legend: { className: "mb-4" } }}
      >
        <FieldRow>
          <FormField
            name={PackagingInfoFormFieldIds.Uoms}
            label='Units of Measure'
            required
            render={({ field, fieldState }) => (
              <FormControl>
                <Select
                  {...field}
                  data-testid={PackagingInfoFormFieldIds.Uoms}
                  {...getSelectFieldProps(fieldState, disabled)}
                  value={field.value ? [field.value.us, field.value.metric].join("-") : ""}
                  onChange={(e) => handleUomSelectChange(e, field.onChange)}
                  renderValue={renderUomSelectValue}
                >
                  {UOM_PAIRS.map((uom) => (
                    <MenuItem key={uom.id} value={uom.id}>
                      {uom.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </FieldRow>
      </FieldGroup>

      <FieldGroup
        legend='Individual Standard Packaging'
        data-testid='individual-packaging-section'
        className='gap-0'
        slotProps={{ legend: { className: "mb-4" } }}
      >
        <FieldRow>
          <FormField
            name={PackagingInfoFormFieldIds.PackagingType}
            label='Standard Packaging'
            required
            render={({ field, fieldState }) => (
              <FormControl>
                <Select
                  {...field}
                  data-testid={PackagingInfoFormFieldIds.PackagingType}
                  {...getSelectFieldProps(fieldState, disabled)}
                  renderValue={renderPackagingSelectPlaceholder}
                >
                  {Object.values(ProductPackagingType).map((packagingType) => (
                    <MenuItem key={packagingType} value={packagingType}>
                      {getProductPackagingTypeLabel(packagingType)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </FieldRow>

        <FieldRow>
          <FormField
            name={PackagingInfoFormFieldIds.PackageUsWeight}
            label='US Standard UoM Quantity'
            required
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid={PackagingInfoFormFieldIds.PackageUsWeight}
                {...getNumberTextFieldProps(fieldState, disabled)}
                onChange={(e) => {
                  const formattedValue = formatNumberString(e.target.value)

                  field.onChange(formattedValue)
                }}
              />
            )}
          />
          <FormField
            name={PackagingInfoFormFieldIds.PackageMetricWeight}
            label='Metric Standard UoM Quantity'
            required
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid={PackagingInfoFormFieldIds.PackageMetricWeight}
                {...getNumberTextFieldProps(fieldState, disabled)}
                onChange={(e) => {
                  const formattedValue = formatNumberString(e.target.value)
                  field.onChange(formattedValue)
                }}
              />
            )}
          />
        </FieldRow>

        <DimensionsFieldRow
          lengthFieldName={PackagingInfoFormFieldIds.PackageLength}
          widthFieldName={PackagingInfoFormFieldIds.PackageWidth}
          heightFieldName={PackagingInfoFormFieldIds.PackageHeight}
          uomFieldName={PackagingInfoFormFieldIds.PackageDimensionsUom}
          disabled={disabled}
        />
      </FieldGroup>

      <FieldGroup
        legend='Full Pallet Packaging'
        data-testid='pallet-packaging-section'
        className='gap-0'
        slotProps={{ legend: { className: "mb-4" } }}
      >
        <FieldRow>
          <FormField
            name={PackagingInfoFormFieldIds.PackagesPerPallet}
            label='Quantity per Pallet (Std. Packaging)'
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid={PackagingInfoFormFieldIds.PackagesPerPallet}
                {...getNumberTextFieldProps(fieldState, disabled)}
              />
            )}
          />
        </FieldRow>

        <FieldRow>
          <FormField
            name={PackagingInfoFormFieldIds.PalletUsWeight}
            label='US Standard UoM Quantity'
            required
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid={PackagingInfoFormFieldIds.PalletUsWeight}
                {...getNumberTextFieldProps(fieldState, disabled)}
                onChange={(e) => {
                  const formattedValue = formatNumberString(e.target.value)
                  field.onChange(formattedValue)
                }}
              />
            )}
          />

          <FormField
            name={PackagingInfoFormFieldIds.PalletMetricWeight}
            label='Metric Standard UoM Quantity'
            required
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                data-testid={PackagingInfoFormFieldIds.PalletMetricWeight}
                {...getNumberTextFieldProps(fieldState, disabled)}
                onChange={(e) => {
                  const formattedValue = formatNumberString(e.target.value)
                  field.onChange(formattedValue)
                }}
              />
            )}
          />
        </FieldRow>

        <DimensionsFieldRow
          lengthFieldName={PackagingInfoFormFieldIds.PalletSizeLength}
          widthFieldName={PackagingInfoFormFieldIds.PalletSizeWidth}
          heightFieldName={PackagingInfoFormFieldIds.PalletSizeHeight}
          disabled={disabled}
        />
      </FieldGroup>
      {/* invisible field to handle form-level errors */}
      <input {...register("form")} type='hidden' />
    </Form>
  )
}

export { PackagingInfoFormFieldIds }
export type { PackagingInfoFormProps }
